import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react';

import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import isNil from 'lodash/isNil';

import { Button } from '../../../../../../shared/components';
import { ButtonProps } from '../../../../../../shared/components/button';
import SvgImage from '../../../../../../shared/components/svg-image';
import { ImageName } from '../../../../../../shared/components/svg-image/images';
import { ReleasePhase } from '../../../../../../types';
import { CollapseBreakpoint } from '../../constants';
import { ToolbarContext } from '../../index';
import classes from './style.css';

interface WrapperProps {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

export interface Props {
    iconName?: ImageName;
    icon?: ReactNode;
    onClick?: () => void;
    title: string;
    /** Если задан, подменяет размер из ToolbarContext (удобно вне тулбара, например в PiP). */
    buttonSize?: ButtonProps['size'];
    disabled?: boolean;
    className?: string | null;
    classNameWrapper?: string;
    buttonProps?: Partial<ButtonProps>;
    content?: ReactNode;
    dotType?: 'red' | 'green';
    collapseBreakpoint?: CollapseBreakpoint | null;
    wrapperProps?: WrapperProps;
    releasePhase?: ReleasePhase | null;
    tooltip?: string | null;
    testId?: string;
}

const RecordingDot = () => <div className={cn(classes.statusDot, classes.red)} />;

const GreenDot = () => <div className={cn(classes.statusDot, classes.green)} />;

const ToolbarButton: FunctionComponent<Props> = ({
    onClick,
    iconName,
    icon,
    disabled,
    title,
    buttonSize: buttonSizeProp,
    className,
    classNameWrapper,
    buttonProps,
    content,
    dotType,
    collapseBreakpoint = CollapseBreakpoint.SMALL,
    wrapperProps,
    releasePhase,
    tooltip,
    testId,
}) => {
    const buttonIcon = useMemo(() => {
        if (icon) {
            return icon;
        }

        if (iconName) {
            return <SvgImage imageName={iconName} />;
        }

        return undefined;
    }, [iconName, icon]) as ButtonProps['iconLeft'];

    const { buttonSize: contextButtonSize } = useContext(ToolbarContext);
    const buttonSize = buttonSizeProp ?? contextButtonSize;

    const hasContent = !isNil(content) && content !== false;
    const isCollapsible = Boolean(collapseBreakpoint && hasContent);
    const collapseBreakpointClass = isCollapsible && collapseBreakpoint ? classes[collapseBreakpoint] : undefined;

    const buttonClasses = cn(
        className,
        {
            [classes.collapsible]: isCollapsible,
        },
        collapseBreakpointClass
    );

    const collapsibleContent = useMemo(() => {
        return isCollapsible && content ? <div className={classes.content}>{content}</div> : null;
    }, [content, isCollapsible]);

    const buttonInnerContent = collapsibleContent || content;

    const buttonContent = (
        <>
            {hasContent ? (
                <Button
                    view="neutral"
                    size={buttonSize}
                    iconLeft={buttonIcon}
                    onPress={onClick}
                    className={buttonClasses}
                    isDisabled={disabled}
                    testId={testId}
                    title={!tooltip ? title : undefined}
                    {...{ 'aria-label': title }}
                    {...buttonProps}
                >
                    {buttonInnerContent}
                </Button>
            ) : (
                <Button
                    view="neutral"
                    size={buttonSize}
                    icon={buttonIcon}
                    onPress={onClick}
                    className={buttonClasses}
                    isDisabled={disabled}
                    testId={testId}
                    title={!tooltip ? title : undefined}
                    {...{ 'aria-label': title }}
                    {...buttonProps}
                />
            )}
        </>
    );

    return (
        <div className={cn(classes.container, classNameWrapper)} {...wrapperProps}>
            {tooltip ? (
                <TooltipStateful content={tooltip} trigger="hover" preserveTabIndex={true}>
                    <div tabIndex={-1}>{buttonContent}</div>
                </TooltipStateful>
            ) : (
                buttonContent
            )}
            {releasePhase && (
                <div className={classes.releasePhaseIconContainer}>
                    <SvgImage imageName={releasePhase} />
                </div>
            )}
            {dotType && (
                <div className={classes.dotContainer}>
                    {dotType === 'green' && <GreenDot />}
                    {dotType === 'red' && <RecordingDot />}
                </div>
            )}
        </div>
    );
};
ToolbarButton.displayName = 'ToolbarButton';

export { ToolbarButton };
